/**
 * Shared stale event detection for subscription updates.
 *
 * THREE-LAYER PROTECTION against "jumping" caused by delayed events:
 *
 * 1. Poll Completion Comparison (PRIMARY): Compare event's _fetchedAt timestamp
 *    against when the last poll completed. If the event data was fetched BEFORE
 *    the poll completed, the poll has fresher data - reject the event.
 *    This handles EventBus delays of any duration (17-25s+ observed on view1-api).
 *
 * 2. Poll Grace Period: Brief window after poll STARTS during which events are
 *    ignored. This prevents race conditions while the poll is in flight.
 *
 * 3. Event Age Check (FALLBACK): Events older than MAX_EVENT_AGE_MS are ignored
 *    as a sanity check for events without _fetchedAt or when no poll has occurred.
 *
 * The _fetchedAt timestamp from the backend enables accurate staleness detection
 * by comparing when data was fetched, not when events arrive.
 */

// Grace period after poll STARTS during which subscription events are ignored
// This covers the time while the poll request is in flight
export const STALE_EVENT_GRACE_PERIOD_MS = 5000; // 5 seconds

// Maximum age of an event (based on _fetchedAt) before it's considered stale
// Fallback check for events without _fetchedAt or when no poll has occurred
export const MAX_EVENT_AGE_MS = 60000; // 60 seconds (increased as primary check is now timestamp comparison)

// Tolerance for clock skew between client and server
// Events within this window of poll completion are accepted to handle slight time differences
export const CLOCK_SKEW_TOLERANCE_MS = 2000; // 2 seconds

// Module-level state for tracking poll timestamps
// Shared across all consumers of this utility
let lastPollRefreshTime = 0;
let lastPollCompletionTime = 0;

/**
 * Call this when a poll/refresh STARTS to mark the timestamp.
 * Subscription events within the grace period will be ignored.
 */
export function markPollRefresh(): void {
  lastPollRefreshTime = Date.now();
  console.log(`[StaleEventDetection] Poll refresh started at ${new Date(lastPollRefreshTime).toISOString()}`);
}

/**
 * Call this when a poll/refresh COMPLETES to mark the timestamp.
 * Events with _fetchedAt older than this will be rejected.
 */
export function markPollComplete(): void {
  lastPollCompletionTime = Date.now();
  console.log(`[StaleEventDetection] Poll completed at ${new Date(lastPollCompletionTime).toISOString()}`);
}

/**
 * Check if we're within the grace period after a poll starts.
 * If so, subscription events should be ignored (poll is in flight).
 */
export function isWithinPollGracePeriod(): boolean {
  const timeSincePoll = Date.now() - lastPollRefreshTime;
  return timeSincePoll < STALE_EVENT_GRACE_PERIOD_MS;
}

/**
 * Get time since poll started (for logging purposes)
 */
export function getTimeSincePollRefresh(): number {
  return Date.now() - lastPollRefreshTime;
}

/**
 * Get time since poll completed (for logging purposes)
 */
export function getLastPollCompletionTime(): number {
  return lastPollCompletionTime;
}

/**
 * PRIMARY STALENESS CHECK: Compare event's _fetchedAt against poll completion time.
 * If the event data was fetched BEFORE the poll completed, reject it.
 * The poll has fresher data from the database.
 *
 * Includes tolerance for clock skew between client (poll timestamp) and server (_fetchedAt).
 */
export function isEventOlderThanPoll(fetchedAt: number | undefined): boolean {
  if (!fetchedAt || !lastPollCompletionTime) {
    // No timestamp to compare - can't determine relative staleness
    return false;
  }
  // Add tolerance for potential clock skew between client and server
  // Event must be older than (pollCompletionTime - tolerance) to be rejected
  return fetchedAt < lastPollCompletionTime - CLOCK_SKEW_TOLERANCE_MS;
}

/**
 * Get how much older the event is than the poll (for logging purposes)
 */
export function getEventPollAgeDiff(fetchedAt: number | undefined): number {
  if (!fetchedAt || !lastPollCompletionTime) {
    return 0;
  }
  return lastPollCompletionTime - fetchedAt;
}

/**
 * FALLBACK: Check if an event is stale based on absolute age.
 * Returns true if the event should be ignored.
 */
export function isEventStale(fetchedAt: number | undefined): boolean {
  if (!fetchedAt) {
    // No timestamp available - can't determine staleness, allow the event
    return false;
  }
  const eventAge = Date.now() - fetchedAt;
  return eventAge > MAX_EVENT_AGE_MS;
}

/**
 * Get the age of an event (for logging purposes)
 */
export function getEventAge(fetchedAt: number | undefined): number {
  if (!fetchedAt) {
    return 0;
  }
  return Date.now() - fetchedAt;
}

/**
 * Comprehensive staleness check combining all three layers.
 * Returns an object with the result and reason for logging.
 */
export function checkEventStaleness(
  fetchedAt: number | undefined,
  logPrefix: string = '[StaleEventDetection]'
): { isStale: boolean; reason: string | null } {
  // Layer 1: Check if within poll grace period
  if (isWithinPollGracePeriod()) {
    const timeSincePoll = getTimeSincePollRefresh();
    const reason = `Ignoring event during poll grace period (${Math.round(timeSincePoll)}ms since poll start, grace: ${STALE_EVENT_GRACE_PERIOD_MS}ms)`;
    console.log(`${logPrefix} ${reason}`);
    return { isStale: true, reason };
  }

  // Layer 2 (PRIMARY): Compare event's _fetchedAt against poll completion time
  if (fetchedAt && isEventOlderThanPoll(fetchedAt)) {
    const pollAge = getEventPollAgeDiff(fetchedAt);
    const reason = `Ignoring event older than poll (event fetched ${Math.round(pollAge)}ms before poll completed)`;
    console.log(`${logPrefix} ${reason}`);
    return { isStale: true, reason };
  }

  // Layer 3 (FALLBACK): Check absolute event age
  if (fetchedAt && isEventStale(fetchedAt)) {
    const eventAge = getEventAge(fetchedAt);
    const reason = `Ignoring stale event (age: ${Math.round(eventAge)}ms, max: ${MAX_EVENT_AGE_MS}ms)`;
    console.log(`${logPrefix} ${reason}`);
    return { isStale: true, reason };
  }

  return { isStale: false, reason: null };
}
